import React, { useState, useEffect, useRef, useMemo } from 'react';
import './AnotherOnePiece.css'; // New CSS file for AnotherOnePiece
import animeConfig from '../config/animeConfig'; // Correct relative path

// firebase adding this // 

import { db } from "../firebase"; // Adjust path as needed
import { collection, addDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  trackGameStart,
  trackGameAnswer,
  trackGameOver,
  trackGamePass,
  trackGameRestart,
  trackGameTimeout,
} from "../utils/analytics";
import SEOHead from "../components/SEO/SEOHead";

const GAME_DISPLAY_NAMES = {
  onePiece: 'One Piece',
  naruto: 'Naruto',
  bleach: 'Bleach',
  onePunch: 'One Punch Man',
  dragonBall: 'Dragon Ball',
};

const AnotherOnePiece = ({ animeIds, theme }) => {
  console.log('=== COMPONENT DEBUG ===');
  console.log('Theme received:', theme);
  console.log('AnimeIds received:', animeIds);
  console.log('Config loaded:', animeConfig[theme]);
  console.log('Config keys available:', Object.keys(animeConfig));
  const config = animeConfig[theme]; // Dynamically load assets for the given theme
  const gameName = GAME_DISPLAY_NAMES[theme] || theme;
  const gameStartTime = useRef(null);
  

  const [characters, setCharacters] = useState([]); // Initialize as empty array
  const [animeData, setAnimeData] = useState({}); // Initialize as empty object
  const [similarAnime, setSimilarAnime] = useState([]); // Initialize as empty array
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [isAnswered, setIsAnswered] = useState(false);
  const [choices, setChoices] = useState([]); // Initialize as empty array
  const [currentAnime, setCurrentAnime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [passesUsed, setPassesUsed] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(15);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [showSimilarAnime, setShowSimilarAnime] = useState(false);
  const [introScreen, setIntroScreen] = useState(true); // Add a state to handle the intro screen
  const [timeLeft, setTimeLeft] = useState(15); // Timer state for each question

  const maxQuestions = 15;
  const maxPasses = 6;

  // Use useRef for background music and dynamically update it when the theme changes
const backgroundMusic = useRef(null);

// Reinitialize background music whenever the theme changes
useEffect(() => {
  if (backgroundMusic.current) {
    backgroundMusic.current.pause(); // Stop the current music
    backgroundMusic.current.currentTime = 0; // Reset to the start
  }
  backgroundMusic.current = new Audio(config.backgroundMusic); // Load new music
  backgroundMusic.current.loop = true; // Ensure it loops
  backgroundMusic.current.onerror = () => {
    console.error('Failed to load background music:', config.backgroundMusic);
  };
}, [config.backgroundMusic]);

  // Timer logic
  useEffect(() => {
    if (timeLeft > 0 && !isAnswered && gameStarted && !gameOver) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isAnswered) {
      handleTimeout();
    }
  }, [timeLeft, isAnswered, gameStarted, gameOver]);

  const handleTimeout = () => {
    trackGameTimeout(gameName, answeredQuestions + 1);
    setIncorrectAnswers((prev) => prev + 1);
    setAnsweredQuestions((prev) => prev + 1);
    setQuestionsLeft((prev) => prev - 1);
    setIsAnswered(true);

    if (answeredQuestions + 1 === maxQuestions) {
      setGameOver(true);
    } else {
      setTimeout(() => nextQuestion(), 1000); // Move to next question after a brief pause
    }
  };

  // Create an Audio object for incorrect sound
  const incorrectSound = new Audio(config.incorrectSound); // Dynamic incorrect sound

  // Create an Audio object for game-over sound and loop it
  const gameOverSound = useRef(new Audio(config.gameOverSound)); // Dynamic game-over sound
  gameOverSound.current.loop = true; // Loop the game-over sound

  // Create an Audio object for correct answer sound
  const correctAnswerSound = new Audio(config.correctAnswerSound); // Dynamic correct answer sound

  // Function to fetch game data based on animeId
  const fetchGameData = async (animeId) => {
    try {
      setCurrentAnime(animeId);

      // Fetch all data concurrently
      const [animeResponse, charactersResponse, similarAnimeResponse] = await Promise.all([
        fetch(`https://api.jikan.moe/v4/anime/${animeId}`),
        fetch(`https://api.jikan.moe/v4/anime/${animeId}/characters`),
        fetch(`https://api.jikan.moe/v4/anime/${animeId}/recommendations`),
      ]);

      // Process responses
      const animeDataResponse = await animeResponse.json();
      const charactersData = await charactersResponse.json();
      const similarAnimeData = await similarAnimeResponse.json();

      setAnimeData(animeDataResponse.data);
      setCharacters(charactersData.data || []);
      setSimilarAnime(similarAnimeData.data || []);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (animeIds && animeIds.length > 0) {
      const randomAnimeId = animeIds[Math.floor(Math.random() * animeIds.length)];
      fetchGameData(randomAnimeId);
    }
  }, [animeIds]);

  // Shuffle function to randomize the characters array
  const shuffleCharacters = (characters) => {
    let shuffled = [...characters];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Memoize shuffled characters to avoid reshuffling on every render
  const shuffledCharacters = useMemo(() => {
    return shuffleCharacters(characters);
  }, [characters]);

  useEffect(() => {
    if (shuffledCharacters && shuffledCharacters.length > 0) {
      setChoices(getRandomChoices());
    }
  }, [shuffledCharacters, questionIndex]);

  const getRandomChoices = () => {
    const correctAnswer = shuffledCharacters[questionIndex]?.character?.name;
    if (!correctAnswer) return [];

    const allNames = shuffledCharacters.map((c) => c?.character?.name).filter(Boolean);
    const uniqueNames = [...new Set(allNames)];
    const filteredNames = uniqueNames.filter((name) => name !== correctAnswer);

    // Create random options based on difficulty level
    let randomOptions = [];
    randomOptions = filteredNames.sort(() => 0.5 - Math.random()).slice(0, 3); // Take 3 random options for now

    // Add the correct answer to the choices and shuffle them
    const finalChoices = [correctAnswer, ...randomOptions].sort(() => Math.random() - 0.5);

    // Ensure exactly 4 options are returned
    return finalChoices.slice(0, 4);
  };

  const handleAnswer = (answer) => {
    const isCorrect = answer === shuffledCharacters[questionIndex]?.character?.name;
    const updatedCorrect = isCorrect ? correctAnswers + 1 : correctAnswers;
    const updatedIncorrect = !isCorrect ? incorrectAnswers + 1 : incorrectAnswers;
    const updatedAnswered = answeredQuestions + 1;

    setSelectedAnswer(answer);
    setIsAnswered(true);

    // Track the answer in GA
    trackGameAnswer(gameName, isCorrect, answeredQuestions + 1, shuffledCharacters[questionIndex]?.character?.name);

    // Play the incorrect sound if the answer is incorrect
    if (!isCorrect) {
      incorrectSound.play();
      setIncorrectAnswers(updatedIncorrect);
    }

    if (isCorrect) {
      // Play correct answer sound
      correctAnswerSound.play();
      setScore(score + 1);
      setCorrectAnswers(updatedCorrect);
    }

    if (!gameStarted) {
      setGameStarted(true);
    }

    setAnsweredQuestions(updatedAnswered);
    setQuestionsLeft(maxQuestions - updatedAnswered);

    if (
      updatedCorrect + updatedIncorrect === maxQuestions ||
      passesUsed === maxPasses
    ) {
      setGameOver(true);
    }

    setTimeLeft(15); // Reset timer for next question
  };

  const handlePass = () => {
    if (passesUsed < maxPasses) {
      trackGamePass(gameName, answeredQuestions + 1);
      setPassesUsed(passesUsed + 1);
      nextQuestion();
    }
  };

  const restartCurrentGame = () => {
    trackGameRestart(gameName);
    setIntroScreen(true);
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setCorrectAnswers(0);
    setIncorrectAnswers(0);
    setQuestionIndex(0);
    setAnsweredQuestions(0);
    setQuestionsLeft(maxQuestions);
    setPassesUsed(0);
    setTimeLeft(15); // Reset timer

    gameOverSound.current.pause();
    gameOverSound.current.currentTime = 0;

    backgroundMusic.current.pause();
    backgroundMusic.current.currentTime = 0;

    fetchGameData(animeIds[Math.floor(Math.random() * animeIds.length)]);
  };

  const nextQuestion = () => {
    setQuestionIndex(questionIndex + 1);
    setIsAnswered(false);
    setSelectedAnswer(null);
    setTimeLeft(15); // Reset timer for next question
  };

  const handleStartGame = () => {
    setIntroScreen(false); // Hide the intro screen
    setGameStarted(true); // Start the game
    gameStartTime.current = Date.now();
    trackGameStart(gameName, theme);
    backgroundMusic.current.play().catch((err) => {
      console.error('Autoplay prevented:', err);
    });
  };
  
  // Rest of your code remains unchanged


  useEffect(() => {
    if (gameOver) {
      // Save game result to Firestore
      const result = {
        score,
        correctAnswers,
        incorrectAnswers,
        date: new Date().toISOString(),
        theme: theme, // Add theme field here
      };
  
      const totalTimeSeconds = gameStartTime.current
        ? Math.round((Date.now() - gameStartTime.current) / 1000)
        : 0;

      // Track game over in GA with full details
      trackGameOver(gameName, theme, score, correctAnswers, incorrectAnswers, totalTimeSeconds);

      const saveGameResult = async () => {
        try {
          // Reference to the gameResults collection
          const resultsRef = collection(db, "users", "guest_user", "gameResults");
          await addDoc(resultsRef, result);
          console.log(`Game result saved for ${theme}:`, result);
        } catch (error) {
          console.error(`Error saving game result for ${theme}:`, error);
        }
      };

      saveGameResult(); // Call the save function

      // Play game-over sound and stop background music
      gameOverSound.current.play();
      backgroundMusic.current.pause();
    }
  }, [gameOver, theme]);
  
  

  if (loading) {
    return <p>Loading game data...</p>;
  }

  const character = shuffledCharacters[questionIndex]?.character;
  const imageUrl = character?.images?.jpg?.image_url || 'https://via.placeholder.com/150';

  return (
    <div
    className="new-onepiece-game-container"
    style={{
      backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.9)), url(${config.backgroundImage})`,
    }}
    >
      <SEOHead
        title={`${gameName} Character Guessing Game - ChibiDash`}
        description={`Play the ${gameName} character guessing game on ChibiDash! Test your anime knowledge by identifying ${gameName} characters. 15 questions, timed rounds.`}
        path={`/games/${theme === 'onePiece' ? 'another-one-piece' : theme === 'onePunch' ? 'one-punch' : theme === 'dragonBall' ? 'dragon-ball' : theme}`}
        keywords={`${gameName} quiz, ${gameName} trivia, ${gameName} character quiz, ${gameName} guessing game, anime quiz, anime trivia, ChibiDash`}
        schema={{
          "@context": "https://schema.org",
          "@type": "VideoGame",
          "name": `${gameName} Character Guessing Game`,
          "description": `Test your ${gameName} anime knowledge by identifying characters in this timed guessing game. 15 questions per round.`,
          "url": `https://www.chibidash.com/games/${theme === 'onePiece' ? 'another-one-piece' : theme === 'onePunch' ? 'one-punch' : theme === 'dragonBall' ? 'dragon-ball' : theme}`,
          "genre": "Trivia",
          "gamePlatform": "Web Browser",
          "numberOfPlayers": { "@type": "QuantitativeValue", "value": 1 },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }}
      />
      {introScreen ? (
        <div className="intro-screen">
          <h2 className="new-onepiece-intro-title">HOW MANY CHARACTERS CAN YOU GUESS?</h2>
          <div className="intro-image-container">
            <img src={config.introImage} alt="Game Start" className="intro-image" />
          </div>
          <button className="start-game-button" onClick={handleStartGame}>
            <span className="start-icon">🎮</span> Start Game
          </button>
        </div>
      ) : (
        <div className="new-onepiece-content">
          <p>Anime: {animeData?.title || 'Loading...'}</p>

          {gameStarted && !gameOver && (
            <div className="new-onepiece-game-info-container">
              <div className="new-onepiece-top-info-game">
                <div className="info-row">
                  <div className="info-box">
                    <p>{questionsLeft} Questions Left</p>
                  </div>
                  <div className="info-box">
                    <p>Your Score: {score}</p>
                  </div>
                </div>

                <div className="info-row">
                  <div className="info-box">
                    <p>Correct: {correctAnswers}</p>
                  </div>
                  <div className="info-box">
                    <p>Incorrect: {incorrectAnswers}</p>
                  </div>
                </div>

                <div className="info-row">
                  <div className="info-box">
                    <p>Passes Remaining: {maxPasses - passesUsed}/{maxPasses}</p>
                  </div>
                  {/* Timer Display */}
                  <div className="info-box timer-container">
                    <p className={`timer-display ${timeLeft <= 5 ? 'timer-warning' : ''}`}>
                      Time Left: {timeLeft}s
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="new-onepiece-character-image-container">
            <img src={imageUrl} alt={character?.name || 'No Image Available'} className="new-onepiece-character-image" />
          </div>

          {!gameOver && (
            <div className="new-onepiece-choices-container">
              <div className="choice-row">
                <button
                  className={`new-onepiece-choice-button ${
                    selectedAnswer === choices[0] ? (selectedAnswer === character?.name ? 'correct' : 'incorrect') : ''
                  }`}
                  onClick={() => handleAnswer(choices[0])}
                  disabled={isAnswered}
                >
                  {choices[0]}
                  {selectedAnswer === choices[0] && selectedAnswer === character?.name && (
                    <span className="correct-icon">✔️</span>
                  )}
                  {selectedAnswer === choices[0] && selectedAnswer !== character?.name && (
                    <span className="incorrect-icon">❌</span>
                  )}
                </button>
                <button
                  className={`new-onepiece-choice-button ${
                    selectedAnswer === choices[1] ? (selectedAnswer === character?.name ? 'correct' : 'incorrect') : ''
                  }`}
                  onClick={() => handleAnswer(choices[1])}
                  disabled={isAnswered}
                >
                  {choices[1]}
                  {selectedAnswer === choices[1] && selectedAnswer === character?.name && (
                    <span className="correct-icon">✔️</span>
                  )}
                  {selectedAnswer === choices[1] && selectedAnswer !== character?.name && (
                    <span className="incorrect-icon">❌</span>
                  )}
                </button>
              </div>

              <div className="choice-row">
                <button
                  className={`new-onepiece-choice-button ${
                    selectedAnswer === choices[2] ? (selectedAnswer === character?.name ? 'correct' : 'incorrect') : ''
                  }`}
                  onClick={() => handleAnswer(choices[2])}
                  disabled={isAnswered}
                >
                  {choices[2]}
                  {selectedAnswer === choices[2] && selectedAnswer === character?.name && (
                    <span className="correct-icon">✔️</span>
                  )}
                  {selectedAnswer === choices[2] && selectedAnswer !== character?.name && (
                    <span className="incorrect-icon">❌</span>
                  )}
                </button>
                <button
                  className={`new-onepiece-choice-button ${
                    selectedAnswer === choices[3] ? (selectedAnswer === character?.name ? 'correct' : 'incorrect') : ''
                  }`}
                  onClick={() => handleAnswer(choices[3])}
                  disabled={isAnswered}
                >
                  {choices[3]}
                  {selectedAnswer === choices[3] && selectedAnswer === character?.name && (
                    <span className="correct-icon">✔️</span>
                  )}
                  {selectedAnswer === choices[3] && selectedAnswer !== character?.name && (
                    <span className="incorrect-icon">❌</span>
                  )}
                </button>
              </div>

              <div className="pass-button-container">
                <button className="pass-button" onClick={handlePass} disabled={passesUsed >= maxPasses || isAnswered}>
                  Pass
                </button>
              </div>
            </div>
          )}

          {isAnswered && (
            <div className="new-onepiece-answer-feedback">
              {selectedAnswer === character?.name ? (
                <p className="new-onepiece-correct-answer">Correct!</p>
              ) : (
                <p className="new-onepiece-incorrect-answer">
                  Incorrect! The correct answer is {character?.name}
                </p>
              )}

              <button
                onClick={nextQuestion}
                className={`new-onepiece-next-button ${
                  isAnswered && selectedAnswer === character?.name ? 'correct' : 'incorrect'
                }`}
              >
                {questionIndex + 1 >= maxQuestions ? '> ' : '>'}
              </button>
            </div>
          )}

          {gameOver && (
            <div
              className="new-onepiece-game-over"
              style={{
                backgroundImage: `url(${config.gameOverBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <h3 className="game-over-heading">Game Over!</h3>
              <div className="game-results">
                <p className="result correct">Correct Answers: {correctAnswers}</p>
                <p className="result incorrect">Incorrect Answers: {incorrectAnswers}</p>
              </div>
              <div className="new-onepiece-button-container">
                <button onClick={restartCurrentGame} className="new-onepiece-rematch-button">
                  Start Again
                </button>
              </div>
            </div>
          )}

          {!gameOver && gameStarted && (
            <div className="game-action-buttons-container">
              <div className="action-button-row">
                <button onClick={restartCurrentGame} className="restart-game-button">
                  Restart Game
                </button>

                {isAnswered && (
                  <button onClick={nextQuestion} className="next-question-button">
                    Next Question
                  </button>
                )}
              </div>
            </div>
          )}

          {showSimilarAnime && (
            <div className="new-onepiece-similar-anime-container">
              <h3>Similar Anime:</h3>
              <ul>
                {similarAnime && similarAnime.length > 0 ? (
                  similarAnime.map((anime) => (
                    anime.node && anime.node.url ? (
                      <li key={anime.node.id}>
                        <a href={anime.node.url} target="_blank" rel="noopener noreferrer">
                          {anime.node.title}
                        </a>
                      </li>
                    ) : null
                  ))
                ) : (
                  <p>No similar anime found.</p>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnotherOnePiece;